import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColDef, ICellRendererParams } from 'ag-grid-community'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { api, can } from '../api'
import { ErrorBox } from '../components'
import { dash, type DashSummary, type DashboardSpec, type TemplateParams } from '../dashApi'
import { DataGrid } from '../DataGrid'
import { dt } from '../format'

const slugOf = (title: string) =>
  title
    .toLowerCase()
    .replace(/[åä]/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)

export function Dashboards() {
  const navigate = useNavigate()
  const q = useQuery({ queryKey: ['dashboards'], queryFn: () => dash.list('dashboard') })
  const me = useQuery({ queryKey: ['me'], queryFn: api.me })
  const [creating, setCreating] = useState(false)
  const cols = useMemo<ColDef<DashSummary>[]>(
    () => [
      {
        field: 'title',
        flex: 2,
        cellRenderer: ({ data }: ICellRendererParams<DashSummary>) =>
          data ? (
            <span className="cell-two">
              <Link to={`/dashboards/${data.slug}`}>{data.title}</Link>
              <span className="muted mono">{data.slug}</span>
            </span>
          ) : null,
      },
      { field: 'widgets', width: 110, cellClass: 'num' },
      { field: 'current_version', headerName: 'Version', width: 100, valueFormatter: (p) => `v${p.value}`, cellClass: 'mono' },
      { field: 'updated_at', headerName: 'Changed', width: 160, valueFormatter: (p) => dt(p.value) },
    ],
    [],
  )
  return (
    <section className="page">
      <header className="page-h">
        <div>
          <h1>Dashboards</h1>
          <p className="sub">
            Charts over your buckets. Measures, filters and series are CEL expressions, the same language as build specs. Widgets
            refresh when a bucket they read is rebuilt. Export a dashboard as PDF, or send it on a schedule as a report.
          </p>
        </div>
        <div className="row">
          <Link className="btn" to="/reports">
            Reports
          </Link>
          {can(me.data, 'editor') && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              New dashboard
            </button>
          )}
        </div>
      </header>
      {creating && <NewDashboard onClose={() => setCreating(false)} />}
      {q.error && <ErrorBox error={q.error} />}
      <DataGrid<DashSummary>
        rows={q.data}
        columnDefs={cols}
        loading={q.isLoading}
        getRowId={(d) => d.slug}
        searchText={(d) => `${d.title} ${d.slug}`}
        onOpen={(d) => navigate(`/dashboards/${d.slug}`)}
      />
    </section>
  )
}

const isDate = (t: string) => t.startsWith('DATE') || t.startsWith('TIMESTAMP')
const isNumber = (t: string) => ['BIGINT', 'INTEGER', 'DOUBLE'].includes(t)

function NewDashboard({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const templates = useQuery({ queryKey: ['dash-templates'], queryFn: dash.templates, staleTime: Infinity })
  const buckets = useQuery({ queryKey: ['dash-buckets'], queryFn: dash.buckets, staleTime: 5 * 60_000 })
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [touched, setTouched] = useState(false)
  const [tpl, setTpl] = useState('')
  const [p, setP] = useState<Omit<TemplateParams, 'template' | 'title'>>({ bucket: '', date: '' })
  const effective = touched ? slug : slugOf(title)
  const template = templates.data?.find((t) => t.id === tpl)
  const bucket = buckets.data?.find((b) => b.slug === p.bucket)
  const cols = bucket?.columns ?? []
  const pickBucket = (slug: string) => {
    const b = buckets.data?.find((x) => x.slug === slug)
    const c = b?.columns ?? []
    setP({
      bucket: slug,
      date: c.find((x) => isDate(x.type))?.name ?? '',
      category: c.find((x) => x.type === 'VARCHAR')?.name,
      region: c.find((x) => /region|county|lan|kommun|municipality/.test(x.name) && x.type === 'VARCHAR')?.name,
      region_level: 'lan',
      region_key: 'code',
    })
  }
  const m = useMutation({
    mutationFn: () => {
      if (template) return dash.fromTemplate({ ...p, template: template.id, title, slug: effective })
      const spec: DashboardSpec = { title, palette: 'aleva', filters: [], widgets: [] }
      return dash.create('dashboard', effective, spec, 'created')
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards'] })
      navigate(`/dashboards/${effective}?edit=1`)
    },
  })
  const field = (label: string, value: string | undefined, set: (v: string) => void, ok: (t: string) => boolean) => (
    <label>
      {label}
      <select value={value ?? ''} onChange={(e) => set(e.target.value)}>
        <option value="">Choose…</option>
        {cols.filter((c) => ok(c.type)).map((c) => (
          <option key={c.name} value={c.name}>
            {c.name} ({c.type})
          </option>
        ))}
      </select>
    </label>
  )
  return (
    <div className="card form">
      <h2>New dashboard</h2>
      <div className="form-grid">
        <label>
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <label>
          Slug (the URL)
          <input
            className="mono"
            value={effective}
            onChange={(e) => {
              setTouched(true)
              setSlug(e.target.value)
            }}
          />
        </label>
        <label className="wide">
          Start from
          <select value={tpl} onChange={(e) => setTpl(e.target.value)}>
            <option value="">An empty dashboard</option>
            {(templates.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
        {template && (
          <>
            <p className="note wide">{template.description}</p>
            <label>
              Bucket
              <select value={p.bucket} onChange={(e) => pickBucket(e.target.value)}>
                <option value="">Choose…</option>
                {(buckets.data ?? []).filter((b) => b.columns.some((c) => isDate(c.type))).map((b) => (
                  <option key={b.slug} value={b.slug}>
                    {b.title || b.slug}
                  </option>
                ))}
              </select>
            </label>
            {field('Date', p.date, (v) => setP({ ...p, date: v }), isDate)}
            <label>
              Measure
              <select value={p.measure ?? ''} onChange={(e) => {
                const col = e.target.value.match(/^sum\((\w+)\)$/)?.[1]
                setP({ ...p, measure: e.target.value || undefined, measure_name: col })
              }}>
                <option value="">Number of rows</option>
                {cols.filter((c) => isNumber(c.type)).map((c) => (
                  <option key={c.name} value={`sum(${c.name})`}>
                    Sum of {c.name}
                  </option>
                ))}
              </select>
            </label>
            {template.needs.includes('category') && field('Category', p.category, (v) => setP({ ...p, category: v }), (t) => t === 'VARCHAR')}
            {template.needs.includes('region') && (
              <>
                {field('Region field', p.region, (v) => setP({ ...p, region: v }), (t) => t === 'VARCHAR' || t === 'INTEGER')}
                <label>
                  Regions
                  <select value={`${p.region_level}/${p.region_key}`} onChange={(e) => {
                    const [l, k] = e.target.value.split('/') as ['lan' | 'kommun', 'code' | 'name']
                    setP({ ...p, region_level: l, region_key: k })
                  }}>
                    <option value="lan/code">Län by code (01–25)</option>
                    <option value="lan/name">Län by name</option>
                    <option value="kommun/code">Kommuner by code (0114…)</option>
                    <option value="kommun/name">Kommuner by name</option>
                  </select>
                </label>
              </>
            )}
          </>
        )}
      </div>
      {m.error && <ErrorBox error={m.error} />}
      <div className="row">
        <button className="btn btn-primary" disabled={!title || !effective || m.isPending || (!!template && (!p.bucket || !p.date))} onClick={() => m.mutate()}>
          {template ? 'Create from template' : 'Create and add widgets'}
        </button>
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
      </div>
    </div>
  )
}
